import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box, Button, CircularProgress, Tab, Tabs, TextField, Typography, Alert,
} from '@mui/material'
import LightbulbOutlinedIcon   from '@mui/icons-material/LightbulbOutlined'
import PeopleOutlinedIcon      from '@mui/icons-material/PeopleOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import { supabase } from '../../supabaseClient'

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL      ?? ''
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? ''

const PILLARS = [
  {
    icon:  <LightbulbOutlinedIcon sx={{ fontSize: 18 }} />,
    label: 'Student-Initiated Research',
    line1: 'Every project begins with a question the student actually cares about.',
    line2: 'No assigned topics. No kits. Ownership from the first idea to the final report.',
  },
  {
    icon:  <PeopleOutlinedIcon sx={{ fontSize: 18 }} />,
    label: '16 Years of Proven Mentorship',
    line1: 'Our mentors have guided students to wins at every level — school, district, county, state, national, and international — for over sixteen years.',
    line2: 'Your student works with someone who has been exactly where they are trying to go.',
  },
  {
    icon:  <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />,
    label: 'AI That Teaches, Not Shortcuts',
    line1: 'AI is fully embedded in the program — to scaffold thinking, guide iteration, and keep research honest.',
    line2: 'Students learn responsible AI use as a skill. Every interaction is visible to their mentor.',
  },
  {
    icon:  <EmojiEventsOutlinedIcon sx={{ fontSize: 18 }} />,
    label: 'Built for the Competition Circuit',
    line1: 'Milestones are mapped to real fair deadlines — school, district, county, and beyond.',
    line2: 'Students arrive at each level prepared with a project they can defend.',
  },
]

/* Marketing links in at ?trial=1 — the AI Lab card and the CTAs on
   ardentresearch.org. Cold traffic that lands on the Sign In tab has no
   account to sign in with, so it bounces. Read once, into the initial state,
   rather than in an effect: an effect would render Sign In first and then
   snap the tab across, and a visible flicker on the first frame of the first
   page a stranger ever sees is worse than no deep link at all. */
function initialTab(params: URLSearchParams): 0 | 1 {
  return params.get('trial') === '1' || params.get('tab') === 'trial' ? 1 : 0
}

export default function LoginPage() {
  const [searchParams]                    = useSearchParams()
  const [tab,           setTab]           = useState<0 | 1>(() => initialTab(searchParams))  // 0 = Sign In, 1 = Free Trial

  // Sign in
  const [email,         setEmail]         = useState('')
  const [password,      setPassword]      = useState('')
  const [signInError,   setSignInError]   = useState<string | null>(null)
  const [signInLoading, setSignInLoading] = useState(false)

  // Free trial
  const [studentEmail,  setStudentEmail]  = useState('')
  const [parentEmail,   setParentEmail]   = useState('')
  const [parentPhone,   setParentPhone]   = useState('')
  const [trialError,    setTrialError]    = useState<string | null>(null)
  const [trialLoading,  setTrialLoading]  = useState(false)
  const [trialStep,     setTrialStep]     = useState<'form' | 'otp' | 'done'>('form')
  // true when verify-phone-otp resent an existing student's link rather than
  // sending a fresh invite — the parent portal line below only applies to a
  // first invite, since a resend does not re-send that one.
  const [trialResent,   setTrialResent]   = useState(false)
  const [otp,           setOtp]           = useState('')
  const [otpLoading,    setOtpLoading]    = useState(false)
  const [otpError,      setOtpError]      = useState<string | null>(null)

  function friendlySignInError(msg: string): string {
    const m = msg.toLowerCase()
    if (m.includes('invalid login') || m.includes('invalid credentials') || m.includes('wrong password'))
      return 'Incorrect email or password. Please try again.'
    if (m.includes('not confirmed') || m.includes('email not confirmed'))
      return 'Your account hasn\u2019t been activated yet. Check your inbox for an invitation email and click the link to set your password.'
    if (m.includes('rate limit') || m.includes('too many') || m.includes('over_email'))
      return 'Too many sign-in attempts. Please wait a few minutes and try again.'
    if (m.includes('network') || m.includes('fetch'))
      return 'Unable to connect. Please check your internet connection and try again.'
    return 'Sign-in failed. Please try again.'
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    setSignInError(null)
    setSignInLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) { setSignInError(friendlySignInError(error.message)); setSignInLoading(false) }
  }

  async function handleTrialSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTrialError(null)
    setTrialLoading(true)
    try {
      let res: Response
      try {
        res = await fetch(`${SUPABASE_URL}/functions/v1/send-phone-otp`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
          body:    JSON.stringify({ phone: parentPhone }),
        })
      } catch {
        throw new Error('Unable to reach the server. Please check your connection and try again.')
      }
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error ?? 'Could not send verification code. Please try again.')
      setTrialStep('otp')
    } catch (e: any) {
      setTrialError(e.message)
    } finally {
      setTrialLoading(false)
    }
  }

  async function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault()
    setOtpError(null)
    setOtpLoading(true)
    try {
      let res: Response
      try {
        res = await fetch(`${SUPABASE_URL}/functions/v1/verify-phone-otp`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
          body:    JSON.stringify({ phone: parentPhone, code: otp, student_email: studentEmail, parent_email: parentEmail }),
        })
      } catch {
        throw new Error('Unable to reach the server. Please check your connection and try again.')
      }
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error ?? 'Verification failed. Please try again.')
      setTrialResent(!!data?.resent)
      setTrialStep('done')
    } catch (e: any) {
      setOtpError(e.message)
    } finally {
      setOtpLoading(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>

      {/* ── Left: Brand panel ── */}
      <Box
        sx={{
          display:        { xs: 'none', md: 'flex' },
          flexDirection:  'column',
          justifyContent: 'space-between',
          width:          '56%',
          bgcolor:        'primary.main',
          p:              6,
          position:       'relative',
          overflow:       'hidden',
        }}
      >
        {/* Background texture — concentric arcs */}
        <Box aria-hidden sx={{ position: 'absolute', right: -180, top: -180, width: 600, height: 600, borderRadius: '50%', border: '80px solid rgba(255,255,255,0.03)', pointerEvents: 'none' }} />
        <Box aria-hidden sx={{ position: 'absolute', right: -80,  top: -80,  width: 400, height: 400, borderRadius: '50%', border: '60px solid rgba(255,255,255,0.03)', pointerEvents: 'none' }} />

        {/* Wordmark */}
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#FFFFFF', letterSpacing: '-0.01em', lineHeight: 1 }}>
            Ardent
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'secondary.main', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Scholar
          </Typography>
        </Box>

        {/* Hero + Pillars */}
        <Box>
          <Typography variant="h3" sx={{ color: '#FFFFFF', fontWeight: 800, lineHeight: 1.15, mb: 1.5, maxWidth: 440 }}>
            Your research.<br />
            Your voice.<br />
            Your future.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 400, mb: 4.5 }}>
            A structured research program for ambitious students who want to build
            original work — and the record to prove it.
          </Typography>

          {/* Pillars — 2×2 grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            {PILLARS.map((p, i) => {
              const isNew = i === 2
              return (
                <Box
                  key={p.label}
                  sx={isNew ? {
                    bgcolor:      'rgba(200,151,58,0.12)',
                    border:       '1px solid rgba(200,151,58,0.35)',
                    borderRadius: '8px',
                    p:            1.5,
                    position:     'relative',
                  } : {}}
                >
                  {isNew && (
                    <Box sx={{
                      position:   'absolute',
                      top:        -10,
                      left:       12,
                      bgcolor:    'secondary.main',
                      color:      '#FFFFFF',
                      fontSize:   '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      px: 1,
                      py: 0.25,
                      borderRadius: '4px',
                    }}>
                      New
                    </Box>
                  )}
                  {/* Icon + label row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, color: 'secondary.main' }}>
                    {p.icon}
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>
                      {p.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', lineHeight: 1.55 }}>
                    {p.line1}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', lineHeight: 1.55, mt: 0.4 }}>
                    {p.line2}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* Footer */}
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>
          © {new Date().getFullYear()} Ardent Scholar ·{' '}
          <a href="https://ardentresearch.org" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', opacity: 0.7 }}>
            Ardent Research
          </a>
        </Typography>
      </Box>

      {/* ── Right: Login form ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 6 },
          bgcolor: 'background.paper',
        }}
      >
        {/* Mobile wordmark */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: 'primary.main', letterSpacing: '-0.01em', lineHeight: 1 }}>
            Ardent
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'secondary.main', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Scholar
          </Typography>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 380 }}>

          {/* Tab toggle */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v as 0 | 1)}
            sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Sign In" sx={{ fontWeight: 600, flex: 1 }} />
            <Tab label="Free Trial" sx={{ fontWeight: 600, flex: 1, color: 'secondary.main' }} />
          </Tabs>

          {/* ── Sign In ── */}
          {tab === 0 && (
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>Welcome back</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Sign in to continue your research journey.
              </Typography>

              {signInError && <Alert severity="error" sx={{ mb: 3 }}>{signInError}</Alert>}

              <Box component="form" onSubmit={handleSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Email" type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  required fullWidth autoComplete="email" autoFocus
                />
                <TextField
                  label="Password" type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  required fullWidth autoComplete="current-password"
                />
                <Button type="submit" variant="contained" fullWidth disabled={signInLoading} sx={{ mt: 1, py: 1.25 }}>
                  {signInLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
                </Button>
              </Box>
            </>
          )}

          {/* ── Free Trial ── */}
          {tab === 1 && (
            <>
              {/* Step: form */}
              {trialStep === 'form' && (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>Start your free trial</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Try Ardent Scholar at no cost. We'll verify your phone number and send invitations to you and your student.
                  </Typography>

                  {trialError && <Alert severity="error" sx={{ mb: 3 }}>{trialError}</Alert>}

                  <Box component="form" onSubmit={handleTrialSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Student email" type="email" value={studentEmail}
                      onChange={e => setStudentEmail(e.target.value)}
                      required fullWidth autoFocus
                    />
                    <TextField
                      label="Parent email" type="email" value={parentEmail}
                      onChange={e => setParentEmail(e.target.value)}
                      required fullWidth
                    />
                    <TextField
                      label="Parent phone" type="tel" value={parentPhone}
                      onChange={e => setParentPhone(e.target.value)}
                      required fullWidth
                      helperText="We'll send a 6-digit verification code to this number."
                    />
                    <Button type="submit" variant="contained" fullWidth disabled={trialLoading} sx={{ mt: 1, py: 1.25 }}>
                      {trialLoading ? <CircularProgress size={22} color="inherit" /> : 'Send Verification Code'}
                    </Button>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, lineHeight: 1.6 }}>
                    No credit card. No commitment. Our team reviews every application.
                  </Typography>
                </>
              )}

              {/* Step: otp */}
              {trialStep === 'otp' && (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>Verify your number</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    We sent a 6-digit code to <strong>{parentPhone}</strong>. Enter it below.
                  </Typography>

                  {otpError && <Alert severity="error" sx={{ mb: 3 }}>{otpError}</Alert>}

                  <Box component="form" onSubmit={handleOtpSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="6-digit code"
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }}
                      required fullWidth autoFocus
                    />
                    <Button type="submit" variant="contained" fullWidth disabled={otp.length < 6 || otpLoading} sx={{ py: 1.25 }}>
                      {otpLoading ? <CircularProgress size={22} color="inherit" /> : 'Verify & Send Invitations'}
                    </Button>
                    <Button
                      variant="text" size="small" color="inherit"
                      sx={{ color: 'text.secondary' }}
                      disabled={trialLoading}
                      onClick={() => { setOtp(''); setOtpError(null); setTrialStep('form') }}
                    >
                      Wrong number? Go back
                    </Button>
                  </Box>
                </>
              )}

              {/* Step: done */}
              {trialStep === 'done' && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CheckCircleOutlinedIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {trialResent ? 'Your link has been resent.' : 'Your free trial is live.'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {trialResent
                      ? <>We've sent a fresh login link to <strong>{studentEmail}</strong> — check that inbox (and spam folder). It's valid for 24 hours.</>
                      : <>We've sent a login link to <strong>{studentEmail}</strong> to get started.
                          Check your inbox — we've also sent you a link to set up your parent portal account.</>}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

    </Box>
  )
}
